import { FC, useEffect } from 'react';
import { useUpdateEffect } from 'react-use';
import { useDispatch } from 'react-redux';

import { usePaginationContext } from 'utils/contexts/pagination';
import { useSortingContext } from 'utils/contexts/sorting';
import { addNegativeToastWithErrorId } from 'modules/toastContainer';
import { useListSizeQuery } from '../hooks/useListSizeQuery';

import { useListsQuery } from '../hooks/useListsQuery';
import { useListTypeCountQuery } from '../hooks/useListTypeCountQuery';
import { useFilterContext } from './FilterContext';
import { QueryContext } from './QueryContext';
import { useListsQueryErrorId } from '../errorIds';

const displayText = {
  error: 'Could not fetch lists',
};

export const QueryContextProvider: FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  const dispatch = useDispatch();
  const { currentPage, pageSize, setCurrentPage } = usePaginationContext();
  const { sortBy, sortDirection } = useSortingContext();
  const { search, type } = useFilterContext();
  const {
    isLoading,
    isError,
    data,
    refetch: refetchLists,
    error,
  } = useListsQuery({
    currentPage,
    pageSize,
    sortBy,
    sortDirection,
    search,
    type,
  });
  const listSizes = useListSizeQuery(data?.results ?? []);
  const { data: listCountByType = {}, refetch: refetchListTypeCounts } = useListTypeCountQuery();

  useEffect(() => {
    if (!isLoading && isError) {
      dispatch(addNegativeToastWithErrorId(displayText.error, useListsQueryErrorId, error));
    }
  }, [isLoading, isError]);

  useUpdateEffect(() => {
    setCurrentPage(1);
  }, [pageSize, search, type]);

  const refetch = () => {
    refetchListTypeCounts();
    refetchLists();
  };

  return (
    <QueryContext.Provider
      value={{
        isLoading,
        lists: data?.results ?? [],
        totalListCount: data?.total ?? 0,
        listSizes,
        listCountByType,
        refetch,
      }}
    >
      {children}
    </QueryContext.Provider>
  );
};
